import { createUuid } from '@notifi/uuid/browser';
import { useEffect, useMemo, useRef, useState } from 'react';
import { createFileRoute, useBlocker, useRouter } from '@tanstack/react-router';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/atoms/alert-dialog';
import { Button } from '@/components/atoms/button';
import { Input } from '@/components/atoms/input';
import { Label } from '@/components/atoms/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/atoms/select';
import { Textarea } from '@/components/atoms/textarea';
import { DatePicker } from '@/components/molecules/date-picker';
import { ImpactRow } from '@/components/molecules/ImpactRow';
import { SectionCard } from '@/components/molecules/SectionCard';
import { formatCurrency } from '@/components/molecules/financialImpact';
import { FormPageLayout } from '@/components/layouts';
import { ExpenseAttachmentsSection } from '@/components/organisms/expenses/ExpenseAttachmentsSection';
import { useCreateExpense } from '@/hooks/expenses/useCreateExpense';
import { useJobProfitability } from '@/hooks/expenses/useJobProfitability';
import { useJobAppointments } from '@/hooks/jobs/useJobAppointments';
import { useJob } from '@/hooks/jobs/useJob';
import { useUsers } from '@/hooks/settings/useUsers';
import { formatDateTimeInTimeZone } from '@/lib/date-values';
import { useScheduleTimezone } from '@/lib/time/scheduleTimezone';
import type { Attachment } from '@/types/attachment';
import type { CreateExpenseDto, ExpenseStatus, ExpenseType, TeamUser } from '@/types/settings';

export const Route = createFileRoute('/_dashboard/dashboard/jobs/$id/expenses/new')({
  // Optional appointmentId so callers (e.g. the visit-detail Expenses card) can pre-scope
  // the new expense to a specific visit instead of falling back to the first one.
  validateSearch: (search: Record<string, unknown>): { appointmentId?: string } => ({
    appointmentId: typeof search.appointmentId === 'string' ? search.appointmentId : undefined,
  }),
  component: NewExpensePage,
});

const EXPENSE_TYPES: { value: ExpenseType; label: string }[] = [
  { value: 'MATERIALS', label: 'Materials' },
  { value: 'EQUIPMENT_RENTAL', label: 'Equipment Rental' },
  { value: 'SUBCONTRACTOR', label: 'Subcontractor' },
  { value: 'TRAVEL', label: 'Travel' },
  { value: 'PERMIT', label: 'Permit' },
  { value: 'DISPOSAL', label: 'Disposal' },
  { value: 'OTHER', label: 'Other' },
];

const REIMBURSE_NONE = '__none__';

interface ExpenseFormState {
  appointmentId: string;
  expenseType: ExpenseType;
  name: string;
  description: string;
  quantity: string;
  unitCost: string;
  vendor: string;
  date: string;
  reimburseToUserId: string;
}

function dateToLocalInput(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function localInputToDate(value: string): Date | undefined {
  const [y, m, d] = value.split('-').map(Number);
  if (!y || !m || !d) return undefined;
  return new Date(y, m - 1, d);
}

function fmtAppointment(startAt?: string | null, timeZone?: string | null): string {
  if (!startAt) return 'Needs scheduling';
  try {
    return formatDateTimeInTimeZone(startAt, timeZone, {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return startAt;
  }
}

function userName(user: TeamUser): string {
  const fullName = [user.firstName, user.lastName].filter(Boolean).join(' ').trim();
  return (user.displayName ?? fullName) || user.email;
}

function serializeForm(form: ExpenseFormState): string {
  return JSON.stringify(form);
}

function emptyForm(defaultAppointmentId = ''): ExpenseFormState {
  return {
    appointmentId: defaultAppointmentId,
    expenseType: 'MATERIALS',
    name: '',
    description: '',
    quantity: '1',
    unitCost: '0',
    vendor: '',
    date: dateToLocalInput(new Date()),
    reimburseToUserId: '',
  };
}

function NewExpensePage() {
  // Resolved PER APPOINTMENT: a job can hold visits in different site
  // zones, so one page-wide zone misprints the out-of-zone ones.
  const { zoneFor } = useScheduleTimezone();
  const { id } = Route.useParams();
  const { appointmentId: appointmentIdParam } = Route.useSearch();
  const router = useRouter();
  const allowNextNavigationRef = useRef(false);
  const initialSnapshotRef = useRef(serializeForm(emptyForm(appointmentIdParam ?? '')));
  const { data: job, isLoading: isJobLoading } = useJob(id);
  const { data: appointments, isLoading: appointmentsLoading } = useJobAppointments(id);
  const { data: usersData } = useUsers();
  const { data: profitability } = useJobProfitability(id);
  const create = useCreateExpense(id);

  const [exitConfirmOpen, setExitConfirmOpen] = useState(false);
  const [pendingId] = useState(() => createUuid());
  const [pendingAttachments, setPendingAttachments] = useState<Attachment[]>([]);
  const [form, setForm] = useState<ExpenseFormState>(() => emptyForm(appointmentIdParam ?? ''));

  const appointmentOptions = useMemo(() => appointments ?? [], [appointments]);
  const users = usersData?.data ?? [];
  const qtyValue = Number(form.quantity || '0');
  const costValue = Number(form.unitCost || '0');
  const total = useMemo(() => {
    return Number.isFinite(qtyValue) && Number.isFinite(costValue) ? qtyValue * costValue : 0;
  }, [costValue, qtyValue]);
  const isReimbursable = Boolean(form.reimburseToUserId);
  const currentCosts = profitability?.expenses ?? 0;
  const newCosts = currentCosts + total;
  const revenue =
    profitability?.invoiced && profitability.invoiced > 0
      ? profitability.invoiced
      : (job?.grandTotal ?? 0);
  const newMargin = revenue > 0 ? ((revenue - newCosts) / revenue) * 100 : 0;
  const canSaveDraft = Boolean(form.appointmentId && form.name.trim());
  const canSubmit =
    canSaveDraft &&
    Number.isFinite(qtyValue) &&
    qtyValue > 0 &&
    Number.isFinite(costValue) &&
    costValue > 0 &&
    Boolean(form.date);
  const isSaving = create.isPending;
  const isDirty = serializeForm(form) !== initialSnapshotRef.current;
  const hasUnsavedWork = isDirty && !isSaving;
  const jobFinancialsRoute = {
    to: '/dashboard/jobs/$id',
    params: { id },
    search: { tab: 'costs' as const },
  };
  const subtitle = job
    ? `${job.jobNumber} · ${job.jobType.name} · current total ${formatCurrency(job.grandTotal ?? 0)}`
    : undefined;

  useEffect(() => {
    void router.navigate({
      to: '/dashboard/jobs/$id',
      params: { id },
      search: {
        tab: 'costs' as const,
        expenseDrawer: 'new' as const,
        ...(appointmentIdParam ? { appointmentId: appointmentIdParam } : {}),
      },
      replace: true,
    });
  }, [appointmentIdParam, id, router]);

  useEffect(() => {
    if (appointmentOptions.length === 0) return;
    // Keep a valid pre-scoped appointment (from ?appointmentId=); otherwise — empty, or a
    // param that doesn't belong to this job — fall back to the first visit.
    const isValid =
      form.appointmentId && appointmentOptions.some((a) => a.id === form.appointmentId);
    if (isValid) return;
    const next = { ...form, appointmentId: appointmentOptions[0].id };
    setForm(next);
    initialSnapshotRef.current = serializeForm(emptyForm(appointmentOptions[0].id));
  }, [appointmentOptions, form]);

  const blocker = useBlocker({
    disabled: !hasUnsavedWork,
    enableBeforeUnload: false,
    withResolver: true,
    shouldBlockFn: () => {
      if (allowNextNavigationRef.current) {
        allowNextNavigationRef.current = false;
        return false;
      }
      return true;
    },
  });

  function update<K extends keyof ExpenseFormState>(key: K, value: ExpenseFormState[K]) {
    setForm((current) => ({ ...current, [key]: value }));
  }

  function leaveWithoutPrompt() {
    allowNextNavigationRef.current = true;
    void router.navigate(jobFinancialsRoute);
  }

  function handleExit() {
    if (hasUnsavedWork) {
      setExitConfirmOpen(true);
      return;
    }
    leaveWithoutPrompt();
  }

  function closeExitConfirm() {
    setExitConfirmOpen(false);
    if (blocker.status === 'blocked') blocker.reset();
  }

  function confirmExit() {
    setExitConfirmOpen(false);
    if (blocker.status === 'blocked') {
      blocker.proceed();
      return;
    }
    leaveWithoutPrompt();
  }

  function buildPayload(status: ExpenseStatus): CreateExpenseDto {
    return {
      appointmentId: form.appointmentId,
      expenseType: form.expenseType,
      name: form.name.trim(),
      description: form.description.trim() || undefined,
      quantity: Number(form.quantity || '0'),
      unitCost: Number(form.unitCost || '0'),
      vendor: form.vendor.trim() || undefined,
      date: form.date,
      reimburseToUserId: form.reimburseToUserId || undefined,
      status,
      pendingId,
    };
  }

  async function save(mode: 'draft' | 'submit') {
    if (!canSaveDraft) return void toast.error('Select an appointment and add a name.');
    if (mode === 'submit' && !canSubmit) {
      return void toast.error('Quantity, unit cost, and date are required to submit.');
    }

    await create.mutateAsync(buildPayload(mode === 'submit' ? 'PENDING' : 'DRAFT'));
    leaveWithoutPrompt();
  }

  if (isJobLoading || appointmentsLoading) {
    return (
      <FormPageLayout
        title="New expense"
        onBack={() => void router.navigate(jobFinancialsRoute)}
        noscroll
      >
        <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
          <Loader2 className="mr-2 size-4 animate-spin" />
          Loading expense form...
        </div>
      </FormPageLayout>
    );
  }

  return (
    <>
      <FormPageLayout
        title="New expense"
        subtitle={subtitle}
        onBack={handleExit}
        noscroll
        actions={
          <>
            <Button type="button" variant="outline" size="sm" onClick={handleExit}>
              Cancel
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={!canSaveDraft || isSaving}
              onClick={() => void save('draft')}
            >
              {isSaving && <Loader2 className="size-3.5 animate-spin" />}
              Save draft
            </Button>
            <Button
              type="button"
              size="sm"
              disabled={!canSubmit || isSaving}
              onClick={() => void save('submit')}
            >
              {isSaving && <Loader2 className="size-3.5 animate-spin" />}
              Save expense
            </Button>
          </>
        }
      >
        <div className="h-full overflow-y-auto">
          <div className="mx-auto grid max-w-[1120px] gap-4 px-4 py-4 sm:px-6 lg:grid-cols-[minmax(0,760px)_300px]">
            <div className="space-y-4">
              <SectionCard className="border-l-4 border-l-foreground/70">
                <div className="space-y-3">
                  <Label className="text-xs font-semibold uppercase tracking-wide">
                    Expense details <span className="text-destructive">· Required</span>
                  </Label>

                  <div className="space-y-1.5">
                    <Label htmlFor="appointment">Appointment</Label>
                    <Select
                      value={form.appointmentId}
                      onValueChange={(value) => update('appointmentId', value)}
                    >
                      <SelectTrigger id="appointment" className="w-full">
                        <SelectValue placeholder="Select appointment" />
                      </SelectTrigger>
                      <SelectContent>
                        {appointmentOptions.map((appointment) => (
                          <SelectItem key={appointment.id} value={appointment.id}>
                            {fmtAppointment(appointment.startAt, zoneFor(appointment.siteTimezone))}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="grid gap-3 sm:grid-cols-[1fr_180px]">
                    <div className="space-y-1.5">
                      <Label htmlFor="name">Name</Label>
                      <Input
                        id="name"
                        value={form.name}
                        onChange={(event) => update('name', event.target.value)}
                        placeholder="e.g. Replacement valve"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label>Type</Label>
                      <Select
                        value={form.expenseType}
                        onValueChange={(value) => update('expenseType', value as ExpenseType)}
                      >
                        <SelectTrigger className="w-full">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {EXPENSE_TYPES.map((type) => (
                            <SelectItem key={type.value} value={type.value}>
                              {type.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="space-y-1.5">
                    <Label htmlFor="description">
                      Description <span className="text-muted-foreground">(optional)</span>
                    </Label>
                    <Textarea
                      id="description"
                      value={form.description}
                      onChange={(event) => update('description', event.target.value)}
                      placeholder="Add any details — vendor, reason, PO number, who paid, etc."
                      rows={3}
                    />
                  </div>

                  <div className="grid gap-3 sm:grid-cols-3">
                    <div className="space-y-1.5">
                      <Label htmlFor="quantity">Qty</Label>
                      <Input
                        id="quantity"
                        type="number"
                        min="0.01"
                        step="0.01"
                        value={form.quantity}
                        onChange={(event) => update('quantity', event.target.value)}
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="unit-cost">Unit cost</Label>
                      <div className="relative">
                        <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
                          $
                        </span>
                        <Input
                          id="unit-cost"
                          type="number"
                          min="0.01"
                          step="0.01"
                          value={form.unitCost}
                          onChange={(event) => update('unitCost', event.target.value)}
                          className="pl-7"
                        />
                      </div>
                    </div>
                    <div className="space-y-1.5">
                      <Label>Date</Label>
                      <DatePicker
                        value={localInputToDate(form.date)}
                        onChange={(date) => date && update('date', dateToLocalInput(date))}
                        placeholder="Expense date"
                      />
                    </div>
                  </div>
                </div>
              </SectionCard>

              <SectionCard title="Reimbursement">
                <div className="mt-4 space-y-3">
                  <div className="space-y-1.5">
                    <Label>Reimburse to</Label>
                    <Select
                      value={form.reimburseToUserId || REIMBURSE_NONE}
                      onValueChange={(value) =>
                        update('reimburseToUserId', value === REIMBURSE_NONE ? '' : value)
                      }
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={REIMBURSE_NONE}>Not reimbursable</SelectItem>
                        {users.map((user) => (
                          <SelectItem key={user.id} value={user.id}>
                            {userName(user)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Select the user who paid out of pocket. Leave as "Not reimbursable" if paid with
                    company card.
                  </p>
                </div>
              </SectionCard>

              <SectionCard title="Receipts & vendor">
                <div className="mt-4 space-y-4">
                  <div className="space-y-1.5">
                    <Label htmlFor="vendor">Vendor</Label>
                    <Input
                      id="vendor"
                      value={form.vendor}
                      onChange={(event) => update('vendor', event.target.value)}
                      placeholder="Vendor or supplier"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Receipts</Label>
                    <ExpenseAttachmentsSection
                      pendingId={pendingId}
                      attachments={pendingAttachments}
                      onAttachmentsChange={setPendingAttachments}
                    />
                    <p className="text-xs text-muted-foreground">
                      Take a photo or upload images / PDFs (max 5MB each).
                    </p>
                  </div>
                </div>
              </SectionCard>
            </div>

            <aside className="lg:sticky lg:top-4 lg:self-start">
              <section className="rounded-lg bg-muted/70 p-4 shadow-xs">
                <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                  Financial impact
                </h2>

                <div className="mt-5 space-y-4 text-sm">
                  <ImpactRow label="Amount" value={total} tone="neutral" total />
                  <ImpactRow
                    label="Reimburse"
                    value={isReimbursable ? total : 0}
                    tone={isReimbursable ? 'warning' : 'neutral'}
                    placeholder={isReimbursable ? undefined : '-'}
                    total={isReimbursable}
                  />
                  <div className="border-t pt-4">
                    <ImpactRow label="Net cost to company" value={total} prominent total />
                  </div>
                </div>

                <h3 className="mt-6 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  Impact on job profitability
                </h3>
                <div className="mt-3 space-y-3 text-sm">
                  <ImpactRow label="Current job costs" value={currentCosts} tone="neutral" total />
                  <ImpactRow
                    label="New job costs"
                    value={newCosts}
                    tone={newCosts > currentCosts ? 'danger' : 'neutral'}
                    total
                  />
                  <div className="border-t pt-3">
                    <ImpactRow
                      label="New margin"
                      value={newMargin}
                      tone={newMargin > 0 ? 'success' : 'danger'}
                      isPercent
                    />
                  </div>
                </div>
                <p className="mt-4 text-xs text-muted-foreground">
                  Current revenue: {formatCurrency(revenue)}
                </p>
              </section>
            </aside>
          </div>
        </div>
      </FormPageLayout>

      <AlertDialog
        open={exitConfirmOpen || blocker.status === 'blocked'}
        onOpenChange={(open) => {
          if (!open) closeExitConfirm();
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Leave without saving?</AlertDialogTitle>
            <AlertDialogDescription>
              You have unsaved changes in this expense. Leaving now will discard them.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={closeExitConfirm}>Stay</AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              onClick={(event) => {
                event.preventDefault();
                confirmExit();
              }}
            >
              Leave without saving
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
